import { nextTick } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import { isLineShape, isOpenShape } from '@/helpers/shapeGeometry'
import type {
  TAmazonCover,
  TAmazonElement,
  TAmazonElementImage,
  TAmazonElementShape,
  TAmazonElementText,
  TAmazonElementUpdate,
} from '@/types/amazon-cover'
import type { TShapeAppearance, TShapeControlRanges, TShapeControlValues, TShapePreset } from '@/types/shapes'
import type { TSize } from '@/types/geometry'

const shapeSizeRatio = 0.2
const lineLengthRatio = 1 / 3
const lineThickness = 3
const lineStrokeWidth = 0.8
const defaultFill = '#006efe'
const defaultStroke = '#1f2937'
const rotateZPattern = /rotateZ\(\s*(-?\d+(?:\.\d+)?)deg\s*\)/i

export function focusEditableText(elementsRef: Map<string, HTMLElement>, elementId: string): void {
  nextTick(() => {
    const editableElement = elementsRef.get(elementId)?.querySelector<HTMLElement>('[data-editable]')
    if (!editableElement) return

    editableElement.setAttribute('contenteditable', 'true')
    editableElement.focus()
  })
}

export function isCustomElement(element: TAmazonElement): boolean {
  return element.type.startsWith('custom-')
}

export function isCoverShape(element: TAmazonElement): element is TAmazonElementShape {
  return element.type === 'custom-shape'
}

export function isRotatableCoverElement(element: TAmazonElement): element is TAmazonElementImage | TAmazonElementShape {
  return element.type === 'image' || element.type === 'custom-image' || element.type === 'custom-shape'
}

export function clonedElementType(element: TAmazonElement): TAmazonElement['type'] {
  if (isCoverShape(element)) return 'custom-shape'

  return 'src' in element ? 'custom-image' : 'custom-text'
}

export function coverShapeSize(preset: TShapePreset, coverWidth: number): TSize {
  if (isLineShape(preset.kind)) return { width: coverWidth * lineLengthRatio, height: lineThickness }

  const base = coverWidth * shapeSizeRatio

  if (preset.ratio >= 1) return { width: base, height: base / preset.ratio }

  return { width: base * preset.ratio, height: base }
}

export function coverShapePresetPayload(
  preset: TShapePreset,
  cover: TSize,
  zIndex: number,
  colors: string[],
): TAmazonElementShape {
  const { width, height } = coverShapeSize(preset, cover.width)
  const color = colors[0]

  const appearance = isOpenShape(preset.kind)
    ? { strokeColor: color ?? defaultStroke, strokeWidth: lineStrokeWidth }
    : { fill: color ?? defaultFill, strokeColor: defaultStroke, strokeWidth: 0 }

  return {
    id: uuidv4(),
    type: 'custom-shape',
    shape: preset.kind,
    group: 'none',
    enabled: true,
    zIndex,
    left: cover.width / 2 - width / 2,
    top: cover.height / 2 - height / 2,
    width,
    height,
    strokeStyle: 'solid',
    cornerRadius: preset.radiusRatio ? Math.round(Math.min(width, height) * preset.radiusRatio * 10) / 10 : undefined,
    lineStart: preset.lineStart,
    lineEnd: preset.lineEnd,
    ...appearance,
  }
}

export function coverShapeAppearance(element: TAmazonElementShape): TShapeAppearance {
  return {
    fill: element.fill,
    strokeColor: element.strokeColor,
    strokeWidth: element.strokeWidth,
    strokeStyle: element.strokeStyle,
    radius: element.cornerRadius,
    flipH: element.flipH,
    flipV: element.flipV,
    lineStart: element.lineStart,
    lineEnd: element.lineEnd,
  }
}

export function coverShapeControlValues(element: TAmazonElementShape): TShapeControlValues {
  return { ...coverShapeAppearance(element), rotation: element.rotation, shadow: element.shadow }
}

export function coverShapeControlRanges(element: TAmazonElementShape): TShapeControlRanges {
  return {
    strokeMax: 10,
    strokeStep: 0.1,
    radiusMax: Math.round((Math.min(element.width, element.height) / 2) * 10) / 10,
    shadowOffsetMax: 10,
    shadowBlurMax: 20,
    shadowInitial: '1px 1px 2px rgba(0,0,0,0.25)',
  }
}

export function coverShapeControlUpdate(partial: TShapeControlValues): TAmazonElementUpdate {
  const { radius, ...rest } = partial

  return 'radius' in partial ? { ...rest, cornerRadius: radius } : rest
}

export function legacyRotation(transform?: string): number | undefined {
  const match = transform ? rotateZPattern.exec(transform) : null

  return match ? parseFloat(match[1] ?? '0') : undefined
}

function normalizeImage(element: TAmazonElementImage): TAmazonElementImage {
  if (element.transform === undefined) return element

  const { transform, ...rest } = element
  const rotation = rest.rotation ?? legacyRotation(transform)

  return rotation ? { ...rest, rotation } : rest
}

export function normalizeCoverContent(content: TAmazonElement[]): TAmazonElement[] {
  return content.map((element) => ('src' in element ? normalizeImage(element) : element))
}

export function coverKeywordsText(cover: Pick<TAmazonCover, 'content' | 'title' | 'project'>): string {
  const textOf = (type: TAmazonElement['type']) =>
    cover.content.find((el): el is TAmazonElementText => el.type === type && 'textContent' in el)?.textContent.trim() ??
    ''
  const subject = textOf('title') || textOf('summary') || cover.title.trim()
  const tags = cover.project?.tags?.map((tag) => tag.name).join(', ') ?? ''

  if (!tags) return subject

  return subject ? `${subject}. Tags: ${tags}` : tags
}
